import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { DollarSign, Search, AlertCircle } from 'lucide-react';
import { WorkOrder } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (args: {
    workOrderId: number;
    type: 'deposit' | 'progress' | 'change' | 'final';
    depositPercentage?: number;
    description?: string;
  }) => void;
  isCreating?: boolean;
}

const CreateInvoiceModal: React.FC<Props> = ({ isOpen, onClose, onCreate, isCreating }) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrder | null>(null);
  const [selectedType, setSelectedType] = useState<'deposit' | 'progress' | 'change' | 'final' | null>(null);
  const [depositPct, setDepositPct] = useState<number>(50);
  const [description, setDescription] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Debounce search to avoid querying on every keystroke
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  const { data: workOrdersResp, isLoading: isWoLoading } = useQuery({
    queryKey: ['work-orders', 'create-invoice', debouncedSearch],
    queryFn: () => apiService.getWorkOrders(1, 50, { searchTerm: debouncedSearch || undefined }),
    keepPreviousData: true
  });

  // Load invoices for selected work order to determine if a deposit already exists
  const { data: woInvoices } = useQuery({
    queryKey: ['wo-invoices', selectedWorkOrder?.id],
    queryFn: async () => {
      if (!selectedWorkOrder) return [] as any[];
      const res = await apiService.getInvoicesForWorkOrder(selectedWorkOrder.id);
      return res as any;
    },
    enabled: !!selectedWorkOrder
  });

  const workOrders = useMemo(() => {
    const resp: any = workOrdersResp;
    let list: WorkOrder[] = [];
    if (resp?.data?.data) {
      list = resp.data.data as WorkOrder[];
    } else if (resp?.data?.items) {
      list = resp.data.items as WorkOrder[];
    } else if (Array.isArray(resp?.data)) {
      list = resp.data as WorkOrder[];
    } else if (Array.isArray(resp?.items)) {
      list = resp.items as WorkOrder[];
    }
    const nonDraft = list.filter(wo => (wo.status || '').toLowerCase() !== 'draft');
    if (!debouncedSearch) return nonDraft;
    const s = debouncedSearch.toLowerCase();
    return nonDraft.filter(wo =>
      `${wo.workOrderNumber || ''}`.toLowerCase().includes(s) ||
      (wo.clientName || '').toLowerCase().includes(s) ||
      (wo.clientAddress || '').toLowerCase().includes(s) ||
      (wo.clientCity || '').toLowerCase().includes(s) ||
      (wo.clientState || '').toLowerCase().includes(s) ||
      (wo.clientZipCode || '').toLowerCase().includes(s)
    );
  }, [workOrdersResp, debouncedSearch]);

  const depositExists = useMemo(() => {
    const list: any[] = Array.isArray(woInvoices) ? (woInvoices as any[]) : [];
    return list.some(inv => (inv.invoiceTypeName || '').toLowerCase() === 'deposit');
  }, [woInvoices]);

  const resetState = () => {
    setStep(1);
    setSearch('');
    setSelectedWorkOrder(null);
    setSelectedType(null);
    setDepositPct(50);
    setDescription('');
    setError('');
  };

  const handleClose = () => {
    if (isCreating) return;
    resetState();
    onClose();
  };

  const handleNext = () => {
    if (!selectedWorkOrder) {
      setError('Please select a work order to continue.');
      return;
    }
    setError('');
    setStep(2);
  };

  const handleCreate = () => {
    if (!selectedWorkOrder || !selectedType) return;
    if (selectedType === 'deposit') {
      if (depositPct < 1 || depositPct > 100) {
        setError('Deposit percentage must be between 1 and 100.');
        return;
      }
    }
    setError('');
    onCreate({
      workOrderId: selectedWorkOrder.id,
      type: selectedType,
      depositPercentage: selectedType === 'deposit' ? depositPct : undefined,
      description: selectedType === 'progress' || selectedType === 'change' ? description || undefined : undefined
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create Invoice" maxWidth="4xl">
      <div className="space-y-6">
        {/* Stepper */}
        <div className="flex items-center justify-center gap-4 text-sm">
          <div className={`px-3 py-1 rounded-full ${step === 1 ? 'bg-accent-color text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>Step 1: Select Work Order</div>
          <div className={`px-3 py-1 rounded-full ${step === 2 ? 'bg-accent-color text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>Step 2: Select Invoice Type</div>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Work Order
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none z-10" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by work order #, client, or address..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-accent-color focus:border-accent-color"
                />
              </div>
            </div>

            <div className="border border-gray-300 dark:border-gray-600 rounded-md max-h-96 overflow-y-auto">
              {isWoLoading && (
                <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">Loading work orders…</div>
              )}
              {!isWoLoading && workOrders.length === 0 && (
                <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">No work orders found.</div>
              )}
              {!isWoLoading && workOrders.map((wo, index) => (
                <button
                  key={wo.id}
                  onClick={() => setSelectedWorkOrder(wo)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    selectedWorkOrder?.id === wo.id ? 'bg-accent-color/10 border-l-4 border-accent-color' : ''
                  } ${index !== 0 ? 'border-t border-gray-200 dark:border-gray-600' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          WO #{wo.workOrderNumber}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          (wo.status || '').toLowerCase() === 'completed'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {wo.status}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-gray-700 dark:text-gray-300">{wo.clientName}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {[wo.clientAddress, wo.clientCity, wo.clientState, wo.clientZipCode].filter(Boolean).join(', ')}
                      </div>
                    </div>
                    {selectedWorkOrder?.id === wo.id && (
                      <div className="ml-2 text-accent-color">
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                        </svg>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-300 p-3 rounded">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button onClick={handleClose} className="px-4 py-2 rounded-md border dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
              <button onClick={handleNext} className="px-4 py-2 rounded-md bg-accent-color text-white hover:bg-accent-color/90">Next</button>
            </div>
          </div>
        )}

        {step === 2 && selectedWorkOrder && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {([
                { key: 'deposit', title: 'Deposit Invoice', desc: 'Create a deposit invoice for upfront payment', color: 'border-blue-400', disabled: depositExists, extra: (
                  <div className="mt-3">
                    <label className="text-xs text-gray-600 dark:text-gray-400">Deposit %</label>
                    <input type="number" min={1} max={100} value={depositPct} onChange={(e)=>setDepositPct(parseInt(e.target.value || '0'))} className="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded" />
                    {depositExists && (
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">A deposit invoice already exists for this work order.</div>
                    )}
                  </div>
                )},
                { key: 'progress', title: 'Progress Invoice', desc: 'Bill for work completed to date', color: 'border-green-400', extra: (
                  <div className="mt-3">
                    <label className="text-xs text-gray-600 dark:text-gray-400">Description (optional)</label>
                    <input value={description} onChange={(e)=>setDescription(e.target.value)} className="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded" />
                  </div>
                )},
                { key: 'change', title: 'Change Order Invoice', desc: 'Bill for additional work or changes', color: 'border-orange-400', extra: (
                  <div className="mt-3">
                    <label className="text-xs text-gray-600 dark:text-gray-400">Description (optional)</label>
                    <input value={description} onChange={(e)=>setDescription(e.target.value)} className="mt-1 w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded" />
                  </div>
                )},
                { key: 'final', title: 'Final Invoice', desc: 'Create final invoice for completed work', color: 'border-purple-400' }
              ] as any[]).map(card => (
                <button
                  key={card.key}
                  onClick={() => { if (!card.disabled) setSelectedType(card.key); }}
                  disabled={!!card.disabled}
                  className={`text-left p-4 border-2 rounded-lg transition-colors ${card.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow'} ${selectedType === card.key ? `${card.color} ring-2 ring-offset-1` : 'border-gray-200 dark:border-gray-700'}`}
                >
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{card.title}</div>
                  </div>
                  <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">{card.desc}</div>
                  {card.key === 'final' && (
                    <div className="mt-2 text-xs text-orange-600 dark:text-orange-400">Ensure deposit has been applied if one exists.</div>
                  )}
                  {card.extra}
                </button>
              ))}
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-300 p-3 rounded">
                <AlertCircle className="h-4 w-4 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <div className="flex justify-between gap-3">
              <button onClick={()=>setStep(1)} className="px-4 py-2 rounded-md border dark:border-gray-600 text-gray-700 dark:text-gray-300">Back</button>
              <div className="flex gap-3">
                <button onClick={handleClose} className="px-4 py-2 rounded-md border dark:border-gray-600 text-gray-700 dark:text-gray-300">Cancel</button>
                <button onClick={handleCreate} disabled={!selectedType || isCreating} className="px-4 py-2 rounded-md bg-accent-color text-white hover:bg-accent-color-600 disabled:opacity-50">
                  {isCreating ? 'Creating...' : 'Create Invoice'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CreateInvoiceModal;

